import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
import DeployLegalFooter from "../../components/deploy/DeployLegalFooter";

const SECTIONS = [
  { id: "overview",    label: "Overview" },
  { id: "quickstart",  label: "Quick start" },
  { id: "tools",       label: "Tools" },
  { id: "pricing",     label: "Pricing" },
  { id: "faq",         label: "FAQ" },
];

const TOOLS = [
  {
    name: "deploy",
    phase: 1,
    desc: "Deploy an app from a git repository (or a pre-built artifact) onto AWS with nginx reverse proxy, HTTPS, and health checks. Runtime, port, and start command are auto-detected (node, python, go, rust, static, docker) — override only when needed.",
    args: [
      { name: "project_name", required: true, desc: "Unique project name (lowercase, hyphens)" },
      { name: "repo_url", required: false, desc: "HTTPS git URL — required unless artifact_id is set" },
      { name: "artifact_id", required: false, desc: "Pre-built artifact from get_artifact_upload_url (skips clone + install)" },
      { name: "tier", required: false, desc: "budget (shared instance) | premium (dedicated, default)" },
      { name: "branch", required: false, desc: "Branch to deploy (default main)" },
      { name: "runtime", required: false, desc: "node | python | go | rust | static | docker — override auto-detection" },
      { name: "start_command", required: false, desc: "Override auto-detected start command (also: build_command, port, node_version)" },
      { name: "instance_type", required: false, desc: "AWS instance size (default t3.micro)" },
      { name: "public_hostname", required: false, desc: "FQDN for nginx + Let's Encrypt (DNS must point at the instance)" },
      { name: "repo_private", required: false, desc: "true for private repos — store git credentials in the dashboard first" },
    ],
    example: 'deploy my app from github.com/me/my-saas',
  },
  {
    name: "redeploy",
    phase: 1,
    desc: "Pull the latest code (or a specific commit), rebuild, and restart the app on the existing server with minimal downtime. Faster than a full deploy.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "branch", required: false, desc: "Branch to sync to (default: branch from last deploy)" },
      { name: "commit_sha", required: false, desc: "Git commit to check out (overrides branch tip)" },
      { name: "zero_downtime", required: false, desc: "Default true (graceful reload); false = brief downtime" },
    ],
    example: 'redeploy my-saas with the latest code',
  },
  {
    name: "rollback",
    phase: 1,
    desc: "Roll back to a previous deployment. Container apps re-run a prior image; git/artifact apps restore from a previous S3 snapshot. Defaults to the deployment before the latest.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "target_git_commit_sha", required: false, desc: "Roll back to the deployment matching this git SHA" },
    ],
    example: 'roll back my-saas, the new version is broken',
  },
  {
    name: "restart",
    phase: 1,
    desc: "Restart the application process for a project.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "hard", required: false, desc: "Force kill (brief downtime)" },
    ],
    example: 'restart my-saas',
  },
  {
    name: "destroy",
    phase: 1,
    desc: "Permanently delete a project and its infrastructure. Requires explicit confirmation.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "confirm", required: true, desc: "Must be true to proceed" },
    ],
    example: 'destroy my old staging project',
  },
  {
    name: "status",
    phase: 1,
    desc: "Get health and status for a project: overall status, public URL, last job result, recent jobs, instance type, and SSL expiry.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
    ],
    example: "what's the status of my-saas?",
  },
  {
    name: "list",
    phase: 1,
    desc: "List all deployed projects for this account with status, runtime, and public URL.",
    args: [],
    example: 'list my deployed projects',
  },
  {
    name: "logs",
    phase: 1,
    desc: "View logs for a project: the deploy pipeline timeline (default), or live app / nginx / system logs read from the instance over SSM (read-only, secrets redacted).",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "source", required: false, desc: "pipeline (default) | app | nginx | system | all" },
      { name: "tail", required: false, desc: "Lines to return (max 500)" },
    ],
    example: 'show me the pipeline logs for my-saas',
  },
  {
    name: "diagnose",
    phase: 1,
    desc: "Run health checks and AI root-cause analysis on a project. Checks HTTP, instance, nginx, process, and SSL state, and returns a plain-language diagnosis with a suggested fix.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
    ],
    example: 'diagnose my-saas — users are getting 502 errors',
  },
  {
    name: "analyze_repo",
    phase: 1,
    desc: "AI review of a git repository before deploy. Detects runtime, start/build command, and port. Pass key files (package.json, requirements.txt, …) as snippets for best accuracy.",
    args: [
      { name: "repo_url", required: true, desc: "HTTPS git URL to analyze" },
      { name: "snippets", required: false, desc: "Map of file path → contents for AI-powered detection" },
    ],
    example: 'analyze github.com/me/my-app before I deploy it',
  },
  {
    name: "evaluate_recovery",
    phase: 1,
    desc: "Validate a suggested shell recovery command against platform safety rules (blocklist + confidence threshold) before it is applied. Does not run the command.",
    args: [
      { name: "command", required: true, desc: "Suggested shell command (single line)" },
      { name: "confidence", required: false, desc: "Confidence 0–1; auto-apply only above the threshold" },
    ],
    example: "check if 'pm2 restart app' is safe to auto-apply",
  },
  {
    name: "env",
    phase: 1,
    desc: "List, set, or remove environment variables for a project. Changes take effect on the next deploy or redeploy.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "action", required: true, desc: "set | remove | list" },
      { name: "key", required: false, desc: "Variable name (required for set/remove)" },
      { name: "value", required: false, desc: "Variable value (for set)" },
    ],
    example: 'set DATABASE_URL on my-saas',
  },
  {
    name: "get_artifact_upload_url",
    phase: 1,
    desc: "Get a pre-signed upload URL for a locally built artifact (tar.gz), then deploy with artifact_id instead of repo_url — skips clone and install on the server.",
    args: [
      { name: "project_name", required: true, desc: "Project name to scope the upload" },
    ],
    example: 'I built locally — get me an upload URL for my-saas',
  },
  {
    name: "domain",
    phase: 2,
    desc: "Attach a custom domain you own via DNS delegation: add creates a Route53 zone and returns nameservers to set at your registrar; verify then wires DNS + Let's Encrypt TLS; remove tears it down.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "action", required: true, desc: "add | verify | remove" },
      { name: "domain", required: true, desc: "Apex domain or subdomain FQDN (e.g. mystore.com)" },
    ],
    example: 'point app.mycompany.com at my-saas',
  },
  {
    name: "environment",
    phase: 2,
    desc: "Create, delete, or list preview/staging environments that run alongside production on the same instance.",
    args: [
      { name: "action", required: true, desc: "create | delete | list" },
      { name: "project", required: true, desc: "Project slug" },
      { name: "env_name", required: false, desc: 'Environment name (e.g. "staging") — required for create/delete' },
      { name: "branch", required: false, desc: "Git branch for create (default main)" },
    ],
    example: 'spin up a staging environment for my-saas',
  },
  {
    name: "create_database",
    phase: 3,
    desc: "Provision a database. postgresql, mysql, mongodb, and redis install on the project's own instance (free, co-located); postgresql can also use managed RDS; documentdb provisions a managed cluster. DATABASE_URL is wired into the app automatically.",
    args: [
      { name: "project", required: true, desc: "Project to attach the database to" },
      { name: "engine", required: false, desc: "postgresql (default) | mysql | mongodb | redis | documentdb" },
    ],
    example: 'add a Postgres database to my-saas',
  },
  {
    name: "destroy_database",
    phase: 3,
    desc: "Permanently delete the managed database for a project (RDS or DocumentDB). Requires explicit confirmation.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "engine", required: false, desc: "postgresql (default) | documentdb" },
      { name: "confirm", required: true, desc: "Must be true to delete" },
    ],
    example: 'delete the managed database on my-saas-staging',
  },
  {
    name: "create_storage",
    phase: 3,
    desc: "Create a dedicated private S3 bucket and wire it into the app automatically — scoped IAM credentials are injected into the project env and a redeploy is queued.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
    ],
    example: 'add S3 file storage to my-saas',
  },
  {
    name: "create_cache",
    phase: 3,
    desc: "Provision a managed Redis cache via ElastiCache. Access is VPC-scoped.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "cache_node_type", required: false, desc: "Node class (default cache.t3.micro)" },
    ],
    example: 'add a Redis cache to my-saas',
  },
  {
    name: "backup",
    phase: 3,
    desc: "Create, list, or restore backups. Supports RDS snapshots (restore supported), EC2 AMI creation, and S3 versioning. Restore requires explicit confirmation.",
    args: [
      { name: "action",   required: true,  desc: "create | list | restore" },
      { name: "project",  required: true,  desc: "Project slug" },
      { name: "resource", required: false, desc: "rds (default) | ec2_ami | s3 — restore only supports rds" },
      { name: "confirm_restore", required: false, desc: "Must be true to restore" },
    ],
    example: 'create a backup snapshot of my-saas database',
  },
  {
    name: "enable_cdn",
    phase: 4,
    desc: "Set up a CloudFront distribution in front of the project. Returns the CloudFront URL and enables global edge delivery.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
    ],
    example: 'add a CDN to my-saas',
  },
  {
    name: "cdn_invalidate",
    phase: 4,
    desc: "Invalidate the CloudFront cache for a project. Use after deploying to ensure users see the latest version.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "paths", required: false, desc: 'Path list (e.g. ["/static/*"]); default ["/*"]' },
    ],
    example: 'clear the CDN cache for my-saas',
  },
  {
    name: "setup_cicd",
    phase: 4,
    desc: "Enable auto-redeploy on GitHub push. Generates a webhook URL + secret (shown once) that you add to your repo's webhook settings on GitHub.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "branch", required: false, desc: "Branch that triggers redeploy (default main)" },
      { name: "repo_full_name", required: false, desc: "owner/repo — restrict the webhook to this repository" },
    ],
    example: 'set up auto-deploy for my-saas from my GitHub repo',
  },
  {
    name: "create_cron",
    phase: 4,
    desc: "Create, list, or delete recurring jobs on the project's server using EventBridge Scheduler. Runs a shell command on your schedule (UTC).",
    args: [
      { name: "project",  required: true, desc: "Project slug" },
      { name: "action", required: false, desc: "create (default) | list | delete" },
      { name: "name", required: false, desc: "Job name — required for create/delete" },
      { name: "schedule", required: false, desc: "Five-field cron, UTC (e.g. 0 3 * * *) — required for create" },
      { name: "command",  required: false, desc: "Shell command to run — required for create" },
    ],
    example: 'run my cleanup script every night at 3am',
  },
  {
    name: "setup_email",
    phase: 4,
    desc: "Register an Amazon SES sending domain. Returns DNS records to add at your registrar; optional SMTP credentials for your app.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "domain",  required: true, desc: "Email sending domain (e.g. mail.yourdomain.com)" },
      { name: "create_smtp_credentials", required: false, desc: "Create SMTP credentials (default true when enabled)" },
    ],
    example: 'set up email sending from mail.mycompany.com for my-saas',
  },
  {
    name: "scale",
    phase: 4,
    desc: "Resize the server (vertical: changes the EC2 instance type) or change instance count (horizontal: adjusts Auto Scaling desired capacity).",
    args: [
      { name: "project",       required: true,  desc: "Project slug" },
      { name: "mode",          required: false, desc: "vertical (default) | horizontal" },
      { name: "instance_type", required: false, desc: "New EC2 type for vertical scale (e.g. t3.small)" },
      { name: "desired_capacity", required: false, desc: "Instance count for horizontal scale" },
      { name: "confirm_large_resize", required: false, desc: "Must be true for large vertical jumps" },
    ],
    example: 'scale my-saas up to a t3.small',
  },
  {
    name: "setup_privacy",
    phase: 5,
    desc: "Generate GDPR-ready compliance artifacts: privacy policy page, cookie consent banner, data export/delete endpoint stubs, PII findings, and recommendations — ready for your AI to commit to the repo.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
      { name: "owner_name", required: true, desc: "Legal name of the operator (shown in the policy)" },
      { name: "contact_email", required: true, desc: "Email for GDPR/privacy requests" },
      { name: "jurisdiction", required: false, desc: "eu (default) | eu_uk | eu_uk_ca" },
    ],
    example: 'set up a GDPR privacy policy for my-saas',
  },
  {
    name: "gdpr_status",
    phase: 5,
    desc: "Check the GDPR compliance setup state for a project — what has been generated, when, and what is still missing.",
    args: [
      { name: "project", required: true, desc: "Project slug" },
    ],
    example: 'is my-saas GDPR-ready?',
  },
  {
    name: "open_ticket",
    phase: 6,
    desc: "Open a support ticket with the team — bug, error, complaint, feature request, or question. If it's about a project, live status/logs are auto-attached. Usually returns an immediate AI first response.",
    args: [
      { name: "subject", required: true, desc: "Short summary of the issue" },
      { name: "body", required: true, desc: "Full description" },
      { name: "category", required: false, desc: "bug | error | complaint | feature_request | question | billing | other" },
      { name: "severity", required: false, desc: "low | medium | high | critical" },
      { name: "project", required: false, desc: "Project slug this is about" },
    ],
    example: 'my deploy keeps 502ing — open a support ticket',
  },
  {
    name: "list_tickets",
    phase: 6,
    desc: "List your support tickets, newest first, with an optional status filter.",
    args: [
      { name: "status", required: false, desc: "open | triaging | awaiting_customer | in_progress | resolved | closed" },
    ],
    example: 'any updates on my support tickets?',
  },
  {
    name: "get_ticket",
    phase: 6,
    desc: "Read one support ticket by ref, including the full message thread and replies.",
    args: [
      { name: "ref", required: true, desc: "Ticket ref (e.g. TKT-9F3A21)" },
    ],
    example: 'check ticket TKT-9F3A21 for a reply',
  },
  {
    name: "reply_ticket",
    phase: 6,
    desc: "Add a reply or more detail to an existing support ticket.",
    args: [
      { name: "ref", required: true, desc: "Ticket ref" },
      { name: "body", required: true, desc: "Your reply" },
    ],
    example: 'reply to TKT-9F3A21: the error still happens on Safari',
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-xs border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-2 py-0.5 transition-colors">
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ToolCard({ tool }) {
  return (
    <div id={`tool-${tool.name}`} className="bg-off-black border border-gray-800 rounded-xl p-6 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <code className="text-lime-400 text-base font-mono font-bold">{tool.name}</code>
          <span className="ml-3 text-xs text-silver-dark border border-gray-700 rounded px-1.5 py-0.5">Phase {tool.phase}</span>
        </div>
      </div>
      <p className="text-silver-medium text-sm mb-4 leading-relaxed">{tool.desc}</p>

      {tool.args.length > 0 && (
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-silver-dark mb-2">Parameters</p>
          <div className="space-y-1.5">
            {tool.args.map((a) => (
              <div key={a.name} className="flex items-start gap-2 text-sm">
                <code className="text-lime-300/80 font-mono text-xs w-36 flex-shrink-0 mt-0.5">{a.name}</code>
                <span className={`text-xs flex-shrink-0 mt-0.5 ${a.required ? "text-amber-400" : "text-silver-dark"}`}>
                  {a.required ? "required" : "optional"}
                </span>
                <span className="text-silver-dark text-xs leading-relaxed">{a.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-black/40 rounded-lg px-4 py-2.5">
        <span className="text-lime-500 text-xs mr-2">›</span>
        <span className="text-silver-light text-xs font-mono italic">{tool.example}</span>
      </div>
    </div>
  );
}

export default function LimeliteDeployDocsPage() {
  const [activeSection, setActiveSection] = useState("overview");
  const mcpDocsUrl = typeof window !== "undefined"
    ? `${window.location.origin}/mcp/docs`
    : "https://rivieratech.solutions/mcp/docs";

  return (
    <>
      <SEO
        title="Documentation — Limelite Deploy"
        description="Full documentation for Limelite Deploy. MCP tools, pricing, quick start guide, and FAQ."
        url="/limelite/deploy/docs"
      />
      <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)]">
        <div className="max-w-5xl mx-auto px-4 py-14 flex gap-10">

          {/* Sidebar */}
          <aside className="hidden md:block w-44 flex-shrink-0 sticky top-20 self-start">
            <div className="flex items-center gap-2 mb-6">
              <img src={LimeLogo} alt="" className="w-5 h-5" />
              <span className="text-xs text-lime-500 uppercase tracking-wider">Deploy</span>
            </div>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveSection(s.id);
                    document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                    activeSection === s.id ? "text-lime-400 bg-lime-900/20" : "text-silver-dark hover:text-silver-medium"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </nav>
            <div className="mt-8 pt-6 border-t border-gray-800 space-y-2">
              <Link to="/limelite/deploy/setup" className="block text-xs text-silver-dark hover:text-silver-medium transition-colors">
                Setup guide →
              </Link>
              <Link to="/limelite/deploy/login" className="block text-xs text-silver-dark hover:text-silver-medium transition-colors">
                Sign up →
              </Link>
              <Link to="/contact" className="block text-xs text-silver-dark hover:text-silver-medium transition-colors">
                Support →
              </Link>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">

            {/* LLM tip */}
            <div className="bg-lime-900/10 border border-lime-800/40 rounded-xl px-4 py-3 mb-10 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-lime-400 font-medium mb-0.5">For AI assistants</p>
                <code className="text-silver-medium text-xs font-mono break-all">{mcpDocsUrl}</code>
              </div>
              <CopyButton text={mcpDocsUrl} />
            </div>

            {/* Overview */}
            <section id="overview" className="mb-14">
              <h1 className="text-3xl font-serif font-medium mb-4">Limelite Deploy</h1>
              <p className="text-silver-medium leading-relaxed mb-4">
                Limelite Deploy is a hosted MCP server that lets AI coding tools deploy and manage web applications on AWS infrastructure. You connect your AI tool once, then describe what you want in plain language — deploy, scale, add a database, roll back, set up CI/CD — and it handles the infrastructure.
              </p>
              <p className="text-silver-medium leading-relaxed mb-6">
                Infrastructure runs on AWS eu-north-1 (Stockholm). Billing is in EUR via Stripe. You pay a base subscription plus actual AWS costs with a margin — no hidden fees.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { label: "Works with", value: "Claude Code, Cursor, Windsurf, Cline, and any MCP-compatible tool" },
                  { label: "Infrastructure", value: "AWS EU (eu-north-1) · nginx · HTTPS · health checks" },
                  { label: "Pricing", value: "From €29/month · pay for what you use" },
                ].map((i) => (
                  <div key={i.label} className="bg-off-black border border-gray-800 rounded-lg p-4">
                    <p className="text-silver-dark text-xs uppercase tracking-wider mb-1">{i.label}</p>
                    <p className="text-silver-light text-sm">{i.value}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Quick start */}
            <section id="quickstart" className="mb-14">
              <h2 className="text-2xl font-serif font-medium mb-6">Quick start</h2>
              <ol className="space-y-5">
                {[
                  {
                    n: "1",
                    title: "Create an account",
                    body: <>Go to <Link to="/limelite/deploy/login" className="text-lime-400 hover:text-lime-300 underline">rivieratech.solutions/deploy</Link>, register, and verify your email. Then subscribe and complete Stripe checkout.</>
                  },
                  {
                    n: "2",
                    title: "Create an API key",
                    body: "Open your dashboard. Under API Keys, click Create Key. Copy the key — you'll only see it once."
                  },
                  {
                    n: "3",
                    title: "Add to your AI tool",
                    body: <>Run: <code className="text-lime-400 font-mono text-xs bg-gray-900 rounded px-2 py-0.5">claude mcp add limelite-deploy https://rivieratech.solutions/mcp -t http -H "Authorization: Bearer YOUR_KEY"</code><br/><span className="text-silver-dark text-sm">Or see the <Link to="/limelite/deploy/setup" className="text-lime-400 hover:text-lime-300 underline">setup guide</Link> for Cursor, Windsurf, and others.</span></>
                  },
                  {
                    n: "4",
                    title: "Deploy your first app",
                    body: 'Tell your AI: "Deploy my project from github.com/me/my-repo" — it handles the rest.'
                  },
                ].map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <div className="w-7 h-7 rounded-full bg-lime-900/30 border border-lime-800 text-lime-400 text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {s.n}
                    </div>
                    <div>
                      <p className="font-medium text-silver-light mb-1">{s.title}</p>
                      <p className="text-silver-dark text-sm leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Tools */}
            <section id="tools" className="mb-14">
              <h2 className="text-2xl font-serif font-medium mb-2">Tools</h2>
              <p className="text-silver-dark text-sm mb-8">
                All tools are invoked naturally through your AI. The tool name is shown for reference — you never call it directly.
              </p>
              {TOOLS.map((t) => <ToolCard key={t.name} tool={t} />)}
            </section>

            {/* Pricing */}
            <section id="pricing" className="mb-14">
              <h2 className="text-2xl font-serif font-medium mb-6">Pricing</h2>
              <div className="bg-off-black border border-gray-800 rounded-xl p-6 mb-6">
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold">€29</span>
                  <span className="text-silver-dark">/ month base</span>
                </div>
                <p className="text-silver-medium text-sm mb-6 leading-relaxed">
                  The base subscription covers the platform. AWS costs (EC2, RDS, S3, etc.) are billed separately as metered usage — you pay the actual AWS cost plus our margin. We report usage deterministically, no AI involved in billing.
                </p>
                <ul className="text-sm text-silver-medium space-y-2">
                  {[
                    "Billing in EUR via Stripe",
                    "Usage auto-stops if subscription lapses — you're never charged for infrastructure you can't afford",
                    "Set a monthly budget cap in your dashboard to prevent surprises",
                    "Cancel anytime — your data is retained for 30 days after cancellation",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="text-lime-500 mt-0.5">—</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mb-14">
              <h2 className="text-2xl font-serif font-medium mb-6">FAQ</h2>
              <div className="space-y-5">
                {[
                  {
                    q: "What types of apps can I deploy?",
                    a: "Node.js, Python, Go, Rust, static sites, and Dockerfile-based apps. If it runs on Linux and listens on a port, it deploys. The AI analyzes your repo before deploying and flags any config issues."
                  },
                  {
                    q: "Do I need to know how servers work?",
                    a: "No. That's the point. You describe what you want to your AI coding assistant and it handles nginx, HTTPS, health checks, DNS, and everything else."
                  },
                  {
                    q: "What happens if I can't pay?",
                    a: "If your Stripe subscription lapses, your EC2 instance is stopped (not deleted). You have 30 days to resubscribe and restart. After 30 days the instance is terminated. You'll get email warnings well in advance."
                  },
                  {
                    q: "Where is the infrastructure?",
                    a: "AWS eu-north-1 (Stockholm). If you need a different region, contact us."
                  },
                  {
                    q: "Can I use private GitHub repos?",
                    a: "Yes. Store your git credentials in the dashboard first, then deploy with repo_private set. Tokens are never passed through the MCP connection."
                  },
                  {
                    q: "Is there a free trial?",
                    a: "No free trial currently, but you can create an account and explore the dashboard before subscribing."
                  },
                ].map((f) => (
                  <div key={f.q} className="border-b border-gray-800 pb-5">
                    <p className="font-medium text-silver-light mb-2">{f.q}</p>
                    <p className="text-silver-dark text-sm leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <DeployLegalFooter />
          </main>
        </div>
      </div>
    </>
  );
}
